import React, { useEffect, useRef, useState } from 'react';
import {
  estimatePushAudience,
  launchPushCampaign,
  type PushAudienceFilters,
  type PushAudienceType,
  type PushCampaign,
  type PushSendMode,
} from '../../services/resortAdminApi';
import {
  getPushCampaignFailureMessage,
  PUSH_QUIET_HOURS_NOTICE,
  toPushEstimateRequest,
} from '../../utils/pushCampaignValidation';

interface ConfirmSendDialogProps {
  open: boolean;
  campaignName: string;
  modified: string;
  audienceType: PushAudienceType;
  filters: PushAudienceFilters;
  sendMode: PushSendMode;
  scheduledAt: string | null;
  onLaunched: (campaign: PushCampaign) => void;
  onCancel: () => void;
}

export const ConfirmSendDialog: React.FC<ConfirmSendDialogProps> = props => {
  const [recipients, setRecipients] = useState<number | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const open = props.open;
  const campaignName = props.campaignName;
  const audienceType = props.audienceType;
  const filtersKey = JSON.stringify(props.filters);
  const estimateRequestRef = useRef(toPushEstimateRequest(audienceType, props.filters, campaignName));

  useEffect(() => {
    estimateRequestRef.current = toPushEstimateRequest(audienceType, props.filters, campaignName);
  }, [audienceType, props.filters, campaignName]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setRecipients(null);
    setIsConfirmed(false);
    setError(null);
    setIsEstimating(true);
    estimatePushAudience(estimateRequestRef.current, controller.signal)
      .then(x => {
        if (cancelled) {
          return;
        }
        setIsEstimating(false);
        setRecipients(x.reachable);
      })
      .catch(x => {
        if (cancelled) {
          return;
        }
        setIsEstimating(false);
        setRecipients(null);
        setError(getPushCampaignFailureMessage(x, 'Could not estimate the audience.'));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, campaignName, audienceType, filtersKey]);

  const confirmedCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (confirmedCountRef.current !== recipients) {
      confirmedCountRef.current = recipients;
      setIsConfirmed(false);
    }
  }, [recipients]);

  if (!props.open) {
    return null;
  }

  const canSubmit = recipients != null && recipients > 0 && isConfirmed && !isSubmitting && !isEstimating;

  const handleSubmit = async () => {
    if (submittingRef.current || recipients == null) {
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const campaign = await launchPushCampaign({
        name: props.campaignName,
        modified: props.modified,
        confirmed: true,
        confirmed_recipients: recipients,
        send_mode: props.sendMode,
        scheduled_at: props.scheduledAt,
      });
      props.onLaunched(campaign);
    } catch (failure) {
      setError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'The campaign could not be launched.'));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={props.onCancel} />
      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-lg">
        <div className="space-y-4 px-6 py-5">
          <h3 className="text-lg font-medium text-gray-900">
            {props.sendMode === 'scheduled' ? 'Schedule the campaign' : 'Send the campaign'}
          </h3>

          {isEstimating && <p className="text-sm text-gray-500">Counting the recipients...</p>}

          {!isEstimating && recipients != null && (
            <div className="rounded-lg bg-gray-50 p-4 text-center">
              <div className="text-4xl font-bold text-gray-900">{recipients}</div>
              <div className="text-sm text-gray-600">users will receive this push</div>
            </div>
          )}

          {!isEstimating && recipients === 0 && (
            <p className="text-sm text-red-700">The audience is empty, there is nobody to send to.</p>
          )}

          {props.sendMode === 'scheduled' && props.scheduledAt != null && (
            <p className="text-sm text-gray-600">Sending starts at {props.scheduledAt}.</p>
          )}

          <p className="text-xs text-gray-500">{PUSH_QUIET_HOURS_NOTICE}</p>

          {error != null && (
            <p className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-700">{error}</p>
          )}

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isConfirmed}
              disabled={recipients == null || recipients === 0}
              onChange={x => setIsConfirmed(x.target.checked)}
              className="mt-0.5"
            />
            <span>I confirm sending to {recipients ?? 0} users</span>
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={props.onCancel}
              disabled={isSubmitting}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : props.sendMode === 'scheduled' ? 'Schedule' : 'Send now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
